import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Package, Clock, Calendar, CheckCircle, Zap, LayoutDashboard, Ticket, Search, ClipboardList, FilePlus } from 'lucide-react';
import PageHeader from './common/PageHeader';
import ProductionBoard from './ProductionBoard';
import { useNavigate } from 'react-router-dom';
import { calculateDiscountedPrice } from '../config/pricing';
import { getFeatures, isBetaUser } from '../utils/features';
import { isLiveSub, resolveBillingOwner } from '../utils/billingPolicy';
import { stage3Of, STAGE3_COLOR } from '../utils/shared/clinic-stage';   // 마감 칩 공정 표시(대표 10-13 "옆에 공정이 나오게 — 상태만")

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // 단계별 오픈: A/S 매칭 배너는 4차(매칭) 오픈 또는 베타만
  const [featureFlags, setFeatureFlags] = useState(null);
  useEffect(() => {
    let alive = true;
    getFeatures().then(f => { if (alive) setFeatureFlags(f); });
    return () => { alive = false; };
  }, []);
  const [dashboardData, setDashboardData] = useState({
    allOrders: [],
    todayArrivals: [],
    todayDeadlines: [],
    tomorrowDeadlines: [],
    stats: { total: 0, today: 0, todayDeadline: 0, tomorrowDeadline: 0 }
  });
  const [appSub, setAppSub] = useState(null);
  const subLive = isLiveSub(appSub);
  useEffect(() => {
    // 재료상은 판매자 대시보드로 리다이렉트
    if (user && (user.businessType === 'seller' || user.sellerStatus === 'approved')) {
      navigate('/seller-dashboard', { replace: true });
      return;
    }
    if (user) {
      loadData();
      loadSubscriptionStatus();
    }
    // eslint-disable-next-line
  }, [user]);

  const loadSubscriptionStatus = async () => {
    try {
      const isDental = user.businessType === 'dental' || user.companyBusinessType === 'dental';
      if (!isDental) return;
      // 판정 대상 = 본인 요금표가 있으면 본인, 없으면 소속 업체(companyId) — billingPolicy.resolveBillingOwner (보안 P1-1)
      const userId = (await resolveBillingOwner(user)).ownerUid || user.uid;

      // 구독 로드
      // 이용 중 판정은 billingPolicy.isLiveSub 한 곳 (App.js 잠금과 같은 규칙 — 해지 후 남은 기간·결제 유예 7일 포함).
      //   종전 limit(1)은 임의 문서 1건(만료·해지 문서일 수 있음)을 상태값만으로 판정했다.
      const subSnap = await getDocs(
        query(collection(db, 'appSubscriptions'), where('userId', '==', userId))
      );
      if (!subSnap.empty) {
        const docs = subSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAppSub(docs.find(d => isLiveSub(d)) || docs[0]);
      }
    } catch (e) {
      console.log('구독 상태 로드 실패:', e);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const userId = user.companyId || user.uid;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dayAfter = new Date(today);
      dayAfter.setDate(dayAfter.getDate() + 2);
      // 배송 하루 소요 반영 (2026-08-26 대표 확정): 오늘 배송=납기 내일, 내일 배송=납기 모레 → 조회 창을 3일로
      const day3 = new Date(today);
      day3.setDate(day3.getDate() + 3);

      // 발신 주문
      const sentSnap = await getDocs(
        query(collection(db, 'workOrders'), where('fromUserId', '==', userId), orderBy('createdAt', 'desc'), limit(100))
      );
      const sent = sentSnap.docs.map(d => ({ id: d.id, ...d.data(), orderType: 'sent' }));

      // 수신 주문
      const recvSnap = await getDocs(
        query(collection(db, 'workOrders'), where('toUserId', '==', userId), orderBy('createdAt', 'desc'), limit(100))
      );
      const recv = recvSnap.docs.map(d => ({ id: d.id, ...d.data(), orderType: 'received' }));

      // 전체 주문 (중복 제거)
      const orderMap = new Map();
      [...sent, ...recv].forEach(order => {
        if (!orderMap.has(order.id)) {
          orderMap.set(order.id, order);
        }
      });
      const allOrders = Array.from(orderMap.values())
        .filter(o => o.source !== 'agent_auto')   // 에이전트 수집 의뢰서는 거래명세서 전용 (주문목록과 동일 정책)
        .sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return bDate - aDate;
        });

      // 오늘 접수
      const todayOrders = allOrders.filter(order => {
        const date = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt || 0);
        return date >= today;
      });

      // 마감일 필터 함수
      const getDueDate = (order) => {
        const d = order.dueDate || order.deliveryDate;
        if (!d) return null;
        return d.toDate ? d.toDate() : new Date(d);
      };

      // 마감 대상: 최근 30건 창에 의존하면 오래전 접수 건이 누락됨 →
      // 마감일 범위로 직접 조회해 합침 (toUserId/fromUserId + dueDate 복합 인덱스)
      let deadlineOrders = allOrders;
      try {
        const [dueRecv, dueSent] = await Promise.all([
          getDocs(query(collection(db, 'workOrders'), where('toUserId', '==', userId), where('dueDate', '>=', today), where('dueDate', '<', day3))),
          getDocs(query(collection(db, 'workOrders'), where('fromUserId', '==', userId), where('dueDate', '>=', today), where('dueDate', '<', day3))),
        ]);
        const dueMap = new Map();
        [...dueRecv.docs, ...dueSent.docs].forEach(d => dueMap.set(d.id, { id: d.id, ...d.data() }));
        // 최근 창의 주문도 합류 (dueDate가 문자열(deliveryDate)뿐인 구주문 대비)
        allOrders.forEach(o => { if (!dueMap.has(o.id)) dueMap.set(o.id, o); });
        deadlineOrders = Array.from(dueMap.values());
      } catch (e) {
        console.warn('마감일 조회 폴백(최근 30건 기준):', e?.message);
      }
      // 에이전트 수집 의뢰서는 거래명세서 전용 — 마감/접수 위젯에서 제외
      deadlineOrders = deadlineOrders.filter(o => o.source !== 'agent_auto');

      // 2026-08-27 대표 지시: 오늘 도착분을 별도 카드로 분리 + 완료(status completed — 2026-09-29부터 서버 자동 완료 포함)된 건은 배송 카드에서 제외
      // 2026-10-12 W3(B5): 덴클 앱 1.3.0 홈과 같은 공통 제외(계획 §3-3) — 취소·거절, 메시지형, 환자명 없는 이메일 의뢰서도 마감 칸에서 뺀다
      //   (agent_auto는 바로 위에서 이미 제외). 종전에는 completed만 빼서 취소된 의뢰서가 「오늘 배송」에 남았다.
      const notCompleted = deadlineOrders
        .filter(o => o.status !== 'completed')
        .filter(o => !['cancelled', 'canceled', 'rejected'].includes(o.status || ''))
        .filter(o => o.type !== 'message')
        .filter(o => !(o.source === 'email' && !o.patientName));

      // 오늘 도착분 — 납기가 오늘인 건 (이미 치과에 도착해야 하는 날)
      const todayArrivals = notCompleted.filter(order => {
        const due = getDueDate(order);
        return due && due >= today && due < tomorrow;
      });

      // 오늘 배송 케이스 (2026-08-26 대표 확정: 배송=다음날 도착) — 납기가 내일인 건
      const todayDeadlines = notCompleted.filter(order => {
        const due = getDueDate(order);
        return due && due >= tomorrow && due < dayAfter;
      });

      // 내일 배송 케이스 — 납기가 모레인 건
      const tomorrowDeadlines = notCompleted.filter(order => {
        const due = getDueDate(order);
        return due && due >= dayAfter && due < day3;
      });


      setDashboardData({
        allOrders,
        todayArrivals,
        todayDeadlines,
        tomorrowDeadlines,
        stats: {
          total: allOrders.length,
          today: todayOrders.length,
          todayDeadline: todayDeadlines.length,
          tomorrowDeadline: tomorrowDeadlines.length
        }
      });
    } catch (error) {
      console.error('대시보드 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // (대시보드 완료 버튼 제거 — 2026-09-02 대표 지시. 2026-09-29부터 완료는 서버 자동 처리)

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>대시보드 로딩 중...</p>
        <style>{spinKeyframes}</style>
      </div>
    );
  }

  const isDental = user?.businessType === 'dental' || user?.companyBusinessType === 'dental';
  const formatNum = (n) => (n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const matchOpen = isBetaUser(featureFlags, user) || ((featureFlags?.phase || 1) >= 4);

  return (
    <div style={styles.container}>
      <PageHeader icon={LayoutDashboard} title="대시보드" />

      {/* 🎁 치과: A/S 무료제작 배너 — 4차(매칭) 오픈 또는 베타만 */}
      {isDental && matchOpen && (
        <div
          onClick={() => navigate('/as-remake')}
          style={{
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            background: 'linear-gradient(135deg, #ECFDF5 0%, #EFF6FF 100%)',
            border: '1px solid #A7F3D0', borderRadius: 14, padding: '16px 20px',
            marginBottom: 16, cursor: 'pointer',
          }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Ticket size={22} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#065F46' }}>
              A/S 재제작 상담
            </div>
            <div style={{ fontSize: 13, color: '#374151', marginTop: 2, lineHeight: 1.6 }}>
              덴탈클러스터 의뢰서 기반으로 A/S 보철 재제작을 상담하고, 실력 있는 새 기공소를 만나보세요.
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate('/as-remake'); }}
            style={{ background: '#059669', color: '#fff', border: 'none', padding: '11px 18px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
          >
            A/S 보철 의뢰하기
          </button>
        </div>
      )}

      {/* 🚀 신규 치과: 시작 가이드 (주문이 아직 없을 때) */}
      {isDental && dashboardData.stats.total === 0 && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: '18px 20px', marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            덴탈클러스터 시작하기
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
            {[
              // 기공소 찾기·A/S 재제작은 phase4(matchOpen) 전용 라우트 — 열려있을 때만 노출 (2026-08-15 검수 P2-2, 죽은 리다이렉트 방지)
              ...(matchOpen ? [
                { step: 1, icon: Search, color: '#6366f1', bg: '#eef2ff', title: '기공소 둘러보기', desc: '우리 지역 기공소를 찾아 프로필과 후기를 확인하세요', path: '/clinic/find-lab' },
                { step: 2, icon: ClipboardList, color: '#059669', bg: '#ECFDF5', title: 'A/S 재제작 상담 의뢰', desc: '기공소를 선택해 재제작 상담을 신청하면 덴클이 확인 후 연결해 드립니다', path: '/as-remake' },
              ] : []),
              { step: matchOpen ? 3 : 1, icon: FilePlus, color: '#D97706', bg: '#FFFBEB', title: '정식 주문 작성', desc: '연결된 기공소에 의뢰서를 작성해 거래를 시작하세요', path: '/create-order' },
            ].map(g => {
              const Icon = g.icon;
              return (
                <button key={g.step} onClick={() => navigate(g.path)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, background: g.bg, border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: g.color, color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{g.step}</span>
                    <Icon size={16} color={g.color} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{g.title}</span>
                  </div>
                  <span style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>{g.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 구독 상태 카드 */}
      {isDental && appSub && (
        <div style={styles.subscriptionRow}>
          <div
            style={{
              ...styles.subCard,
              background: subLive
                ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#f1f5f9',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/subscription')}
          >
            <div style={styles.subCardRow}>
              <Zap size={20} color={subLive ? '#fff' : '#94a3b8'} />
              <div style={{ marginLeft: 10, flex: 1 }}>
                <div style={{ fontSize: 11, color: subLive ? 'rgba(255,255,255,0.7)' : '#94a3b8' }}>앱 구독</div>
                <div style={{ fontSize: 17, fontWeight: 'bold', color: subLive ? '#fff' : '#64748b' }}>
                  {subLive ? '구독 중' : '미구독'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 마감 섹션 — 오늘 도착분 분리 (2026-08-27 대표 지시). 칩에 버튼 없음 */}
      <div style={styles.gridTwo}>
        {/* 칩 횡렬 나열 (dashboard_preview_1.html .chip-row/.chip — 2026-08-05 대표 지시) */}
        <DeadlineCard
          title="오늘 도착분"   // 납기가 오늘 = 이미 치과에 도착해야 하는 날 (어제 보냈어야 하는 건)
          icon={Clock}
          color="#ef4444"
          orders={dashboardData.todayArrivals || []}
          chipStyle={{ backgroundColor: 'rgba(208,59,59,0.08)', borderColor: 'rgba(208,59,59,0.35)' }}
          dotColor="#d03b3b"
          nameColor="#d03b3b"
          onView={(id) => navigate('/view-order/' + id)}
        />
        <DeadlineCard
          title="오늘 배송 케이스"   // '마감'→'배송 케이스' 개명 (2026-08-26 대표): 오늘 보내면 내일 납기에 도착
          icon={Package}
          color="#3b82f6"
          orders={dashboardData.todayDeadlines}
          chipStyle={{ backgroundColor: 'rgba(59,130,246,0.08)', borderColor: 'rgba(59,130,246,0.35)' }}
          dotColor="#3b82f6"
          nameColor="#0f172a"
          onView={(id) => navigate('/view-order/' + id)}
        />
        <DeadlineCard
          title="내일 배송 케이스"
          icon={Calendar}
          color="#f59e0b"
          orders={dashboardData.tomorrowDeadlines}
          chipStyle={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}
          dotColor="#f59e0b"
          nameColor="#0f172a"
          onView={(id) => navigate('/view-order/' + id)}
        />
      </div>

      {/* 오늘 완료됨 카드 제거 (2026-09-02 대표 지시) */}

      {/* 제작 현황(캘린더, 2026-09-29 대표 지시 — 종전 보드) — 최근 접수 대체, 기공소·치과 공통 (2026-08-05 대표 지시, ProductionBoard embedded 재사용 — 계정 유형별 뷰는 ProductionBoard가 스스로 분기) */}
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitleWrap}><Package size={18} color="#6366f1" /><span style={styles.cardTitle}>기공 현황</span></div>
          <button onClick={() => navigate('/production')} style={styles.viewAllBtn}>{'전체 보기 →'}</button>
        </div>
        <ProductionBoard user={user} embedded />
      </div>

      <style>{spinKeyframes}</style>
    </div>
  );
}

// 통계 카드 컴포넌트
const StatCard = ({ icon: Icon, color, bgColor, value, label, sub }) => (
  <div style={{ ...styles.statCard, borderLeft: '4px solid ' + color }}>
    <div style={{ ...styles.statIcon, backgroundColor: bgColor }}><Icon size={22} color={color} /></div>
    <div style={styles.statInfo}>
      <span style={styles.statValue}>{value}</span>
      <span style={styles.statLabel}>{label}</span>
      {sub && <span style={styles.statSub}>{sub}</span>}
    </div>
  </div>
);

// 마감 카드 컴포넌트 — 환자 1명 = 칩 1개, flex-wrap 횡렬 (행+시계/달력 배지 반복 제거, 2026-08-05 대표 지시)
// 공정 3단계 표시(읽기 전용 — 누르면 칩 전체가 의뢰서 보기로 감). 판정 = 제작 현황·주문목록과 같은 stage3Of
const StageTag = ({ order }) => {
  const s3 = stage3Of(order);
  const fg = s3.key ? STAGE3_COLOR[s3.key] : '#94a3b8';
  const label = s3.key ? s3.label : '의뢰서 확인 전';
  const tip = s3.autoDue ? '납기일 자정 자동 처리 — 납품·수령을 증명하지 않습니다' : label;
  return <span title={tip} style={{ ...styles.chipStage, background: fg + '14', color: fg }}>{label}</span>;
};

const DeadlineCard = ({ title, icon: Icon, color, orders, chipStyle, dotColor, nameColor, onView }) => (
  <div style={styles.card}>
    <div style={styles.cardHeader}>
      <div style={styles.cardTitleWrap}>
        <Icon size={18} color={color} />
        <span style={styles.cardTitle}>{title}</span>
        <span style={styles.cardCount}>{orders.length + '건'}</span>
      </div>
    </div>
    {orders.length === 0 ? (
      <div style={styles.emptyState}><CheckCircle size={32} color="#10b981" /><p style={{ color: '#10b981', margin: '8px 0 0' }}>{title} 작업 없음</p></div>
    ) : (
      <div style={styles.chipRow}>
        {orders.map((order, idx) => (
          <div key={'dl-' + idx + '-' + order.id} onClick={() => onView(order.id)} style={{ ...styles.chip, ...chipStyle }}>
            <span style={{ ...styles.chipDot, backgroundColor: dotColor }} />
            <span style={{ ...styles.chipName, color: nameColor }}>{order.patientName || '환자명 없음'}</span>
            <span style={styles.chipPartner}>{'· ' + (order.orderType === 'sent' ? (order.toUserName || order.labName || '-') : (order.fromUserName || order.dentistName || '-'))}</span>
            <StageTag order={order} />
          </div>
        ))}
      </div>
    )}
  </div>
);

const spinKeyframes = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9', padding: 16 },

  subscriptionRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 16 },
  subCard: { borderRadius: 14, padding: 16 },
  subCardRow: { display: 'flex', alignItems: 'center' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 },
  statCard: { display: 'flex', alignItems: 'center', gap: 14, padding: 18, backgroundColor: '#fff', borderRadius: 14 },
  statIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statInfo: { display: 'flex', flexDirection: 'column' },
  statValue: { fontSize: 26, fontWeight: 700, color: '#0f172a' },
  statLabel: { fontSize: 13, fontWeight: 600, color: '#64748b' },
  statSub: { fontSize: 11, color: '#9ca3af', marginTop: 2 },

  gridTwo: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginBottom: 16 },

  card: { backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden' },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #f1f5f9', gap: 8 },
  cardTitleWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
  viewAllBtn: { padding: '6px 12px', backgroundColor: '#f8fafc', color: '#6366f1', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', color: '#9ca3af', textAlign: 'center' },

  // 마감 칩 (dashboard_preview_1.html .chip-row/.chip 이식)
  cardCount: { fontSize: 12, fontWeight: 600, color: '#94a3b8' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8, padding: 14 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 999, border: '1px solid', fontSize: 12.5, cursor: 'pointer', maxWidth: '100%' },
  chipDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  chipName: { fontWeight: 700, whiteSpace: 'nowrap' },
  chipStage: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 },
  chipPartner: { fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },


  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 },
  spinner: { width: 40, height: 40, border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { fontSize: 14, color: '#64748b' },
};

export default Dashboard;